import { useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronUp, Mic, Pause, Play } from "lucide-react";
import { cn } from "@/lib/utils";
import type { ChatAttachment } from "./types";

interface Props {
  attachment: ChatAttachment;
  isOwn: boolean;
}

const BAR_COUNT = 28;

export function VoiceNotePlayer({ attachment, isOwn }: Props) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [waveform, setWaveform] = useState<number[] | null>(null);
  const [duration, setDuration] = useState<number>(attachment.durationSeconds ?? 0);
  const [errored, setErrored] = useState(false);
  const [transcriptOpen, setTranscriptOpen] = useState(false);

  // Sample the audio buffer once for a real waveform. Falls back to a
  // hash-based pseudo-waveform if decode fails (CORS, codec, etc.) —
  // playback can still work even if waveform sampling doesn't.
  useEffect(() => {
    if (!attachment.url) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(attachment.url!);
        if (!res.ok) throw new Error("fetch failed");
        const buf = await res.arrayBuffer();
        const ctx = new (window.AudioContext ||
          (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
        const decoded = await ctx.decodeAudioData(buf);
        const channel = decoded.getChannelData(0);
        const blockSize = Math.max(1, Math.floor(channel.length / BAR_COUNT));
        const bars: number[] = [];
        for (let i = 0; i < BAR_COUNT; i++) {
          let max = 0;
          for (let j = 0; j < blockSize; j++) {
            const v = Math.abs(channel[i * blockSize + j] ?? 0);
            if (v > max) max = v;
          }
          bars.push(max);
        }
        const peak = Math.max(...bars, 0.001);
        if (!cancelled) {
          setWaveform(bars.map((v) => v / peak));
          // If the server-side duration was wrong, prefer the decoded one.
          if (decoded.duration && isFinite(decoded.duration)) {
            setDuration(decoded.duration);
          }
        }
      } catch {
        if (!cancelled) setWaveform(fakeWaveform(attachment.id));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [attachment.id, attachment.url]);

  const onTogglePlay = () => {
    const el = audioRef.current;
    if (!el || errored) return;
    if (playing) {
      el.pause();
    } else {
      el.play().catch((err) => {
        console.error("Voice note play failed", err);
        setErrored(true);
      });
    }
  };

  const onTimeUpdate = () => {
    const el = audioRef.current;
    if (!el) return;
    // WebM blobs from MediaRecorder often report Infinity as duration
    // until the file finishes streaming. Use whichever number is real.
    const denom =
      el.duration && isFinite(el.duration) && el.duration > 0
        ? el.duration
        : duration > 0
          ? duration
          : 0;
    if (denom > 0) {
      setProgress(Math.min(1, el.currentTime / denom));
    }
  };

  const bars = waveform ?? fakeWaveform(attachment.id);
  const activeBars = Math.floor(bars.length * progress);

  if (!attachment.url) {
    return <div className="text-xs italic text-muted-foreground">Voice note unavailable</div>;
  }

  const transcript = attachment.transcript;

  return (
    <div className="flex min-w-[220px] flex-col gap-1 px-1 py-1">
      <div className="flex items-center gap-3">
      <button
        type="button"
        onClick={onTogglePlay}
        aria-label={playing ? "Pause voice note" : "Play voice note"}
        className={cn(
          "inline-flex size-8 shrink-0 items-center justify-center rounded-full",
          isOwn
            ? "bg-background/15 text-background hover:bg-background/25"
            : "bg-foreground text-background hover:bg-foreground/85",
        )}
      >
        {playing ? (
          <Pause className="size-3.5 fill-current" />
        ) : (
          <Play className="size-3.5 fill-current" />
        )}
      </button>

      <div className="flex flex-1 items-center gap-[2px]">
        {bars.map((amplitude, i) => {
          const heightPx = 6 + amplitude * 14;
          const isPast = i < activeBars;
          return (
            <span
              key={i}
              className={cn(
                "block w-[2px] rounded-full transition-colors",
                isOwn
                  ? isPast
                    ? "bg-background"
                    : "bg-background/45"
                  : isPast
                    ? "bg-foreground"
                    : "bg-foreground/40",
              )}
              style={{ height: `${heightPx}px` }}
            />
          );
        })}
      </div>

      <div
        className={cn(
          "flex items-center gap-1 text-[11px] tabular-nums",
          isOwn ? "text-background/70" : "text-muted-foreground",
        )}
      >
        <Mic className="size-3" />
        {formatDuration(duration)}
      </div>

      {/*
        Use sr-only (off-screen but functional) instead of `hidden`
        (display:none) — Safari and some Chrome configs disable
        playback on display-none audio elements.
      */}
      <audio
        ref={audioRef}
        src={attachment.url}
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => {
          setPlaying(false);
          setProgress(0);
        }}
        onTimeUpdate={onTimeUpdate}
        onLoadedMetadata={(e) => {
          const d = (e.currentTarget as HTMLAudioElement).duration;
          if (d && isFinite(d) && d > 0) setDuration(d);
        }}
        onError={() => {
          console.error("Voice note audio element errored", attachment.url);
          setErrored(true);
        }}
        className="sr-only"
      />
      </div>

      {transcript && (
        <div className="px-1">
          <button
            type="button"
            onClick={() => setTranscriptOpen((v) => !v)}
            className={cn(
              "inline-flex items-center gap-1 text-[10px] uppercase tracking-wide transition-colors",
              isOwn ? "text-background/60 hover:text-background" : "text-muted-foreground hover:text-foreground",
            )}
          >
            {transcriptOpen ? <ChevronUp className="size-3" /> : <ChevronDown className="size-3" />}
            {transcriptOpen ? "Hide transcript" : "Show transcript"}
          </button>
          {transcriptOpen && (
            <p
              className={cn(
                "mt-1 text-[12px] leading-5",
                isOwn ? "text-background/85" : "text-foreground",
              )}
            >
              {transcript}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export function formatDuration(s: number) {
  if (!s || !isFinite(s)) return "0:00";
  const m = Math.floor(s / 60);
  const r = Math.floor(s % 60);
  return `${m}:${r.toString().padStart(2, "0")}`;
}

export function fakeWaveform(seed: string): number[] {
  let hash = 0;
  for (const c of seed) {
    hash = (hash * 31 + c.charCodeAt(0)) | 0;
  }
  const bars: number[] = [];
  for (let i = 0; i < BAR_COUNT; i++) {
    const x = (hash + i * 17) & 0xff;
    const v = (Math.sin(x * 0.13) + 1) / 2;
    bars.push(0.15 + v * 0.85);
  }
  return bars;
}

export const VOICE_BAR_COUNT = BAR_COUNT;
